import { NgClass } from '@angular/common';
import { Component, DestroyRef, inject, model, type OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { SelectModule } from '@openng/optimus-ui/select';

import { LLM_PROVIDERS } from '@app/modules/settings/components/platform-settings/platform-settings-integrations/platform-settings-integrations.component';
import type { ChatModelOption } from '@app/shared/models/ai-chat.model';
import { AiChatService } from '@app/shared/services/ai-chat.service';
import { StorageService } from '@app/shared/services/storage.service';

/** Provider order for the client-side global default (decision #2): Google -> Azure -> Bedrock. */
const PROVIDER_PRIORITY = ['google', 'azure', 'aws-bedrock'];
const LAST_MODEL_KEY_PREFIX = 'ai-chat:last-model';

/**
 * `provider · model` picker chip shown near the chat prompt box.
 *
 * Loads the configured models, resolves an initial selection (last-used, else the Google→Azure→
 * Bedrock default), and exposes the choice via the two-way `selected` model so the chat view can
 * read `provider`+`model`. The choice is persisted per-user to localStorage on change, so a reload
 * restores the last-used model. An empty model list renders a muted "no models" hint.
 */
@Component({
    selector: 'cor-ai-model-picker',
    templateUrl: './ai-model-picker.component.html',
    imports: [FormsModule, NgClass, SelectModule],
})
export class AiModelPickerComponent implements OnInit {
    /** Two-way bindable selection so the chat view can read the chosen provider+model. */
    readonly selected = model<ChatModelOption | null>(null);

    protected readonly options = signal<ChatModelOption[]>([]);
    protected readonly loading = signal(false);

    private readonly aiChatService = inject(AiChatService);
    private readonly storageService = inject(StorageService);
    private readonly destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        this.loading.set(true);
        this.aiChatService
            .getModels()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ result }) => {
                    const models = this.sortByProvider(result ?? []);
                    this.options.set(models);
                    this.selected.set(this.resolveInitialSelection(models));
                },
            })
            .add(() => this.loading.set(false));
    }

    /** User picked a chip: update the selection and remember it for next time. */
    protected onSelect(option: ChatModelOption): void {
        this.selected.set(option);
        this.storageService.setLocalStorage(
            LAST_MODEL_KEY_PREFIX,
            JSON.stringify({ provider: option.provider, model: option.model }),
        );
    }

    protected providerName(provider: string): string {
        return LLM_PROVIDERS[provider]?.displayName ?? provider;
    }

    protected providerLogo(provider: string): string | null {
        return LLM_PROVIDERS[provider]?.logoClass ?? null;
    }

    /**
     * last-used (if still available) -> first provider in Google→Azure→Bedrock order
     * -> none when the list is empty.
     */
    private resolveInitialSelection(options: ChatModelOption[]): ChatModelOption | null {
        if (options.length === 0) {
            return null;
        }

        const lastUsed = this.readLastUsed();
        if (lastUsed) {
            const match = options.find(
                option => option.provider === lastUsed.provider && option.model === lastUsed.model,
            );
            if (match) {
                return match;
            }
        }

        const topProvider = options[0].provider;
        const withinTopProvider = options.find(option => option.provider === topProvider);
        return withinTopProvider;
    }

    private providerRank(provider: string): number {
        const index = PROVIDER_PRIORITY.indexOf(provider);
        return index === -1 ? PROVIDER_PRIORITY.length : index;
    }

    /** Group models by provider (Google→Azure→Bedrock first, then the rest), each block sorted by model. */
    private sortByProvider(models: ChatModelOption[]): ChatModelOption[] {
        return [...models].sort(
            (a, b) =>
                this.providerRank(a.provider) - this.providerRank(b.provider) ||
                a.provider.localeCompare(b.provider) ||
                a.displayName.localeCompare(b.displayName),
        );
    }

    private readLastUsed(): { provider: string; model: string } | null {
        const raw = this.storageService.getLocalStorage(LAST_MODEL_KEY_PREFIX);
        if (!raw) {
            return null;
        }
        try {
            return JSON.parse(raw) as { provider: string; model: string };
        } catch {
            return null;
        }
    }
}
